import { makeAutoObservable } from "mobx";

import {
  validationContexts,
  ZValidationContext,
} from "./types/ZValidationContext";

type ZDraftSnapshot = {
  templateName: string;
  templateDescription: string;
  serializedFilter: string;
};

type ZNameUniquenessChecker = (name: string) => boolean;

const alwaysUniqueName: ZNameUniquenessChecker = () => true;

type QueryBuilderDraftStoreParams = {
  getSerializedFilter: () => string;
  validateFilterTree: (context: ZValidationContext) => boolean;
};

export class QueryBuilderDraftStore {
  templateName: string = "";

  setTemplateName(value: string): void {
    this.templateName = value;
    this.revalidateIfNeeded();
  }

  templateDescription: string = "";

  setTemplateDescription(value: string): void {
    this.templateDescription = value;
    this.revalidateIfNeeded();
  }

  private activeValidationContext: ZValidationContext | null = null;

  private setActiveValidationContext(value: ZValidationContext | null): void {
    this.activeValidationContext = value;
  }

  private initialDraftSnapshot: ZDraftSnapshot | null = null;

  private setInitialDraftSnapshot(value: ZDraftSnapshot | null): void {
    this.initialDraftSnapshot = value;
  }

  private nameUniquenessChecker: ZNameUniquenessChecker = alwaysUniqueName;

  setNameUniquenessChecker(value: ZNameUniquenessChecker): void {
    this.nameUniquenessChecker = value;
    this.revalidateIfNeeded();
  }

  constructor(private readonly params: QueryBuilderDraftStoreParams) {
    makeAutoObservable(this);
  }

  private clearValidation(): void {
    this.setActiveValidationContext(null);
  }

  private revalidateIfNeeded(): void {
    if (!this.activeValidationContext) {
      return;
    }

    this.validate({ context: this.activeValidationContext });
  }

  reset(): void {
    this.clearValidation();
    this.setTemplateName("");
    this.setTemplateDescription("");
    this.setInitialDraftSnapshot(null);
  }

  private buildDraftSnapshot(): ZDraftSnapshot {
    return {
      templateName: this.templateName.trim(),
      templateDescription: this.templateDescription.trim(),
      serializedFilter: this.params.getSerializedFilter(),
    };
  }

  captureInitialDraft(): void {
    this.setInitialDraftSnapshot(this.buildDraftSnapshot());
  }

  commitDraftSnapshot(): void {
    this.captureInitialDraft();
  }

  get dirtyFields(): {
    templateName: boolean;
    templateDescription: boolean;
    filter: boolean;
  } {
    if (!this.initialDraftSnapshot) {
      return {
        templateName: false,
        templateDescription: false,
        filter: false,
      };
    }

    const current = this.buildDraftSnapshot();
    return {
      templateName:
        current.templateName !== this.initialDraftSnapshot.templateName,
      templateDescription:
        current.templateDescription !==
        this.initialDraftSnapshot.templateDescription,
      filter:
        current.serializedFilter !== this.initialDraftSnapshot.serializedFilter,
    };
  }

  get isDirty(): boolean {
    const fields = this.dirtyFields;
    return fields.templateName || fields.templateDescription || fields.filter;
  }

  private getNameValidationError(
    context: ZValidationContext | null,
  ): string | null {
    const normalizedName = this.templateName.trim();

    if (
      context === validationContexts.saveTemplate &&
      normalizedName.length === 0
    ) {
      return "Необходимо заполнить название";
    }

    if (
      normalizedName.length > 0 &&
      !this.nameUniquenessChecker(normalizedName)
    ) {
      return "Название фильтра должно быть уникальным";
    }

    return null;
  }

  validate(options: { context: ZValidationContext }): boolean {
    this.setActiveValidationContext(options.context);

    return (
      this.params.validateFilterTree(options.context) &&
      this.getNameValidationError(options.context) === null
    );
  }

  get shouldValidateCriterionValue(): boolean {
    return this.activeValidationContext !== validationContexts.saveTemplate;
  }

  get isNameValid(): boolean {
    return this.getNameValidationError(this.activeValidationContext) === null;
  }

  get shouldShowValidationErrors(): boolean {
    return this.activeValidationContext !== null;
  }

  get nameValidationErrorMessage(): string | null {
    return this.getNameValidationError(this.activeValidationContext);
  }

  get shouldShowNameValidationError(): boolean {
    return this.nameValidationErrorMessage !== null;
  }

  revalidateTreeIfNeeded(): void {
    this.revalidateIfNeeded();
  }
}
